import React, { useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Link,
  Loading,
  Message,
  Table,
  Text,
} from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { Trash2 } from 'lucide-react'
import {
  CompanyScope,
  PermissionGrant,
  PrivilegeUser,
} from 'api/admin/userPermissions/types'
import { getPermissionName } from 'api/admin/userPermissions/permissions'
import { useUserPermissionsContextValue } from '../context/UserPermissionsProvider'
import { useUserPermissionsWorkflowsContextValue } from '../context/UserPermissionsWorkflowsProvider'
import { formatCompanyScopeLabel } from '../utils'
import { format, parseISO } from 'date-fns'

function formatTimestamp(value: string | null) {
  if (!value) {
    return '—'
  }
  try {
    return format(parseISO(value), 'MMM d, yyyy HH:mm')
  } catch {
    return value
  }
}

const UserCell = ({ user }: { user: PrivilegeUser }) => {
  const displayName = user.name ?? ''

  return (
    <Text>
      {displayName ? `${displayName} (` : ''}
      <Link to={`/users/${user.id}`} target="_blank">
        {user.id}
      </Link>
      {displayName ? ')' : ''}
    </Text>
  )
}

const GrantMeta = ({
  effectiveAt,
  expiresAt,
  grantedBy,
}: {
  effectiveAt: string
  expiresAt: string | null
  grantedBy: PermissionGrant['grantedBy']
}) => {
  const grantedByLabel = grantedBy.name
    ? `${grantedBy.name} (${grantedBy.id})`
    : String(grantedBy.id)

  return (
    <Text color="lightText" fontSize={1} mt={1}>
      Effective {formatTimestamp(effectiveAt)}
      {' · '}
      {expiresAt ? `Expires ${formatTimestamp(expiresAt)}` : 'Never expires'}
      {' · '}
      Granted by {grantedByLabel}
    </Text>
  )
}

const PermissionRowItem = ({
  user,
  grant,
}: {
  user: PrivilegeUser
  grant: PermissionGrant
}) => {
  const { removeUserPermissionsFlow } =
    useUserPermissionsWorkflowsContextValue()

  return (
    <Flex
      justifyContent="space-between"
      alignItems="flex-start"
      gap={2}
      py={2}
      borderBottom="1px solid"
      borderColor="neutrals.50"
    >
      <Box flex={1} minWidth={0}>
        <Text fontWeight="medium">{getPermissionName(grant.permission)}</Text>
        <GrantMeta
          effectiveAt={grant.effectiveAt}
          expiresAt={grant.expiresAt}
          grantedBy={grant.grantedBy}
        />
      </Box>
      <Button
        variant="text"
        kind="small"
        color="red"
        aria-label={`Revoke ${getPermissionName(grant.permission)}`}
        onClick={() =>
          removeUserPermissionsFlow.open({
            kind: 'permission',
            permissionId: grant.id,
            permission: grant.permission,
            user,
          })
        }
      >
        <Trash2 size={14} />
      </Button>
    </Flex>
  )
}

const CompanyScopeRowItem = ({
  user,
  scope,
  canRevoke,
}: {
  user: PrivilegeUser
  scope: CompanyScope
  canRevoke: boolean
}) => {
  const { removeUserPermissionsFlow } =
    useUserPermissionsWorkflowsContextValue()
  const label = formatCompanyScopeLabel(scope)

  return (
    <Flex justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
      <Box flex={1} minWidth={0}>
        <Text fontWeight="medium">{label}</Text>
        <GrantMeta
          effectiveAt={scope.effectiveAt}
          expiresAt={scope.expiresAt}
          grantedBy={scope.grantedBy}
        />
      </Box>
      {canRevoke ? (
        <Button
          variant="text"
          kind="small"
          color="red"
          aria-label={`Revoke company scope ${label}`}
          onClick={() =>
            removeUserPermissionsFlow.open({
              kind: 'companyScope',
              companyScopeId: scope.id,
              label,
              user,
            })
          }
        >
          <Trash2 size={14} />
        </Button>
      ) : null}
    </Flex>
  )
}

function buildColumns(
  canManageCompanyScopes: boolean
): ColumnDef<PrivilegeUser>[] {
  return [
    {
      header: 'User',
      accessorKey: 'id',
      enableResizing: true,
      minSize: 180,
      cell: ({ row }) => <UserCell user={row.original} />,
    },
    {
      header: 'Permissions',
      accessorKey: 'permissions',
      cell: ({ row }) => {
        const permissions = row.original.permissions
        if (permissions.length === 0) {
          return <Text color="lightText">—</Text>
        }
        return (
          <Box>
            {permissions.map((grant) => (
              <PermissionRowItem
                key={grant.id}
                user={row.original}
                grant={grant}
              />
            ))}
          </Box>
        )
      },
    },
    {
      header: 'Company scopes',
      accessorKey: 'companyScopes',
      cell: ({ row }) => {
        const scopes = row.original.companyScopes
        if (scopes.length === 0) {
          return <Text color="lightText">—</Text>
        }
        return (
          <Box>
            {scopes.map((scope) => (
              <CompanyScopeRowItem
                key={scope.id}
                user={row.original}
                scope={scope}
                canRevoke={canManageCompanyScopes}
              />
            ))}
          </Box>
        )
      },
    },
  ]
}

export const UserPermissionsTable = () => {
  const {
    state: {
      items,
      isPending,
      isError,
      error,
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      canManageCompanyScopes,
    },
    actions: { loadMore },
  } = useUserPermissionsContextValue()

  const columns = useMemo(
    () => buildColumns(canManageCompanyScopes),
    [canManageCompanyScopes]
  )

  if (isPending) {
    return <Loading />
  }

  if (isError) {
    return (
      <Message variant="error" borderRadius="small">
        {error ||
          'Sorry, something went wrong. Please reach out to eng support with the URL of the page if this persists.'}
      </Message>
    )
  }

  if (items.length === 0) {
    return (
      <Flex
        mb={2}
        flexDirection="column"
        alignItems="center"
        color="lightText"
        height={258}
        justifyContent="center"
        maxWidth={['100%', '100%', '50%', '50%']}
        margin="auto"
      >
        <Text>No permission grants found.</Text>
      </Flex>
    )
  }

  return (
    <Box>
      <Table columns={columns} data={items} textVerticalAlign="top" />
      {hasNextPage ? (
        <Flex mt={3}>
          <Button
            variant="secondary"
            loading={isFetchingNextPage || isFetching}
            onClick={loadMore}
          >
            Load more
          </Button>
        </Flex>
      ) : null}
    </Box>
  )
}
